import React, { useState, useEffect, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import { jsPDF } from "jspdf";
import html2canvas from "html2canvas";
import autoTable from "jspdf-autotable";
import { getCollaborateur, getFeuilleTemps, getFeuilleTempsMois, getFeuilleTempsPeriode, getMesProjets } from "../services/api";
import { formatDate } from "../services/projet";
import GraphiqueSecteurs from "../components/GraphiqueSecteurs";
import IconeV2 from "../components/IconeV2";

// ===================================================================
// The Crowd v2 — Pilotage › Equipes › Fiche collaborateur
//
// Adaptation v2 de pages/FicheCollaborateur.js : memes appels API,
// memes calculs, meme tableau croise, meme export PDF.
//
// Adresse propre (/v2/pilotage/equipe/:id) : le retour navigateur
// ramene aux cartes de l'equipe.
//
// Changement de comportement, aligne sur la Saisie des temps v2 :
// la navigation par semaine et par mois passe d'une annee a l'autre
// (numerotation ISO pour les semaines), au lieu de bloquer sur
// l'annee civile en cours.
//
// Refonte du module Projets :
//  - les heures sont regroupees par tache (tache_id), plus par nom : deux
//    etapes homonymes de projets differents restent distinctes, et une
//    etape s'affiche « Projet · Etape » (tableau croise, camembert, PDF) ;
//  - le panneau « Taches projet » (toutes les taches de categorie Projet,
//    desactivees par la reprise) est remplace par « Projets » : les projets
//    du collaborateur, acteur principal ou secondaire (lecture Mes projets).
//  - la periode personnalisee lit la plage de dates elle-meme
//    (feuilles-temps/collaborateur/{id}/periode). Auparavant, elle filtrait
//    le mois selectionne dans la vue Mois : toute plage hors de ce mois
//    restait vide. Semaines clees par annee dans le tableau croise.
// ===================================================================

const MOIS_NOMS = ["Janvier", "Février", "Mars", "Avril", "Mai", "Juin", "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre"];
const JOURS_COURTS = ["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"];
const LIBELLES_ROLES = { directeur: "Directeur", manager: "Manager", collaborateur: "Collaborateur" };
const LIBELLES_ROLE_PROJET = { principal: "Principal", secondaire: "Secondaire" };

// Libelle d'une ligne de temps : « Projet · Etape » pour une etape
function libelleTache(e) {
  if (e.tache_categorie === "Etape projet" && e.projet_nom) return `${e.projet_nom} · ${e.tache_nom}`;
  return e.tache_nom || "Autre";
}

// Heures par tache (cle : tache_id), pour le camembert et le PDF
function repartitionParTache(timeData) {
  const parTache = {};
  timeData.forEach((e) => {
    const cle = e.tache_id ?? libelleTache(e);
    if (!parTache[cle]) parTache[cle] = { label: libelleTache(e), value: 0 };
    parTache[cle].value += e.heures;
  });
  return Object.values(parTache);
}

// ---------------------------------------------------------------------------
// Dates (semaines ISO)
// ---------------------------------------------------------------------------

function semaineIso(d) {
  const date = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
  const jour = date.getUTCDay() || 7;
  date.setUTCDate(date.getUTCDate() + 4 - jour);
  const debutAnnee = new Date(Date.UTC(date.getUTCFullYear(), 0, 1));
  return { annee: date.getUTCFullYear(), semaine: Math.ceil(((date - debutAnnee) / 86400000 + 1) / 7) };
}

function nbSemainesIso(annee) {
  return semaineIso(new Date(annee, 11, 28)).semaine;
}

// Lundi a dimanche de la semaine ISO
function getWeekDays(annee, semaine) {
  const jan1 = new Date(Date.UTC(annee, 0, 1));
  const dayOfWeek = jan1.getUTCDay() || 7;
  const mondayW1 = new Date(jan1);
  mondayW1.setUTCDate(jan1.getUTCDate() + (1 - dayOfWeek) + (dayOfWeek > 4 ? 7 : 0));
  const monday = new Date(mondayW1);
  monday.setUTCDate(mondayW1.getUTCDate() + (semaine - 1) * 7);
  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date(monday);
    d.setUTCDate(monday.getUTCDate() + i);
    return d.toISOString().split("T")[0];
  });
}

function formatJourCourt(dateStr) {
  const d = new Date(dateStr + "T00:00:00Z");
  return `${JOURS_COURTS[(d.getUTCDay() + 6) % 7]} ${d.getUTCDate()}`;
}

// ---------------------------------------------------------------------------
// Tableau croise — calcul partage par l'ecran et par l'export PDF
// Semaine    : colonnes = jours (Lun -> Dim)
// Mois/perso : colonnes = semaines (S19, S20...)
// ---------------------------------------------------------------------------

function construireCroise(timeData, periodType, semaine, annee) {
  // Semaine clee par annee quand elle est connue (plage a cheval sur deux annees)
  const normalized = timeData.map((e) => ({
    ...e,
    dateStr: (e.date_jour || "").split("T")[0],
    sem: e.annee ? e.annee * 100 + e.semaine : e.semaine,
  }));
  // Une ligne par tache (tache_id) : deux etapes homonymes restent distinctes
  const cleDe = (e) => String(e.tache_id ?? libelleTache(e));
  const tachesMap = {};
  normalized.forEach((e) => {
    const cle = cleDe(e);
    if (!tachesMap[cle]) tachesMap[cle] = { nom: libelleTache(e), categorie: e.tache_categorie || "" };
  });
  const taches = Object.keys(tachesMap);
  const total = normalized.reduce((s, e) => s + e.heures, 0);

  let colonnes;
  let valeur;
  let totalColonne;
  if (periodType === "semaine") {
    const jours = getWeekDays(annee, semaine);
    colonnes = jours.map((j) => ({ cle: j, libelle: formatJourCourt(j) }));
    valeur = (cle, j) => normalized.filter((e) => cleDe(e) === cle && e.dateStr === j).reduce((s, e) => s + e.heures, 0);
    totalColonne = (j) => normalized.filter((e) => e.dateStr === j).reduce((s, e) => s + e.heures, 0);
  } else {
    const semaines = [...new Set(normalized.map((e) => e.sem).filter(Boolean))].sort((a, b) => a - b);
    colonnes = semaines.map((s) => ({ cle: s, libelle: `S${s % 100}` }));
    valeur = (cle, s) => normalized.filter((e) => cleDe(e) === cle && e.sem === s).reduce((sum, e) => sum + e.heures, 0);
    totalColonne = (s) => normalized.filter((e) => e.sem === s).reduce((sum, e) => sum + e.heures, 0);
  }

  const lignes = taches
    .map((cle) => {
      const vals = colonnes.map((c) => valeur(cle, c.cle));
      const t = vals.reduce((s, v) => s + v, 0);
      return t === 0 ? null : { cle, nom: tachesMap[cle].nom, categorie: tachesMap[cle].categorie, vals, total: t };
    })
    .filter(Boolean);

  return { colonnes, lignes, totaux: colonnes.map((c) => totalColonne(c.cle)), total };
}

function TableauCroise({ croise }) {
  const { colonnes, lignes, totaux, total } = croise;
  return (
    <table className="v2-tableau v2-croise">
      <thead>
        <tr>
          <th scope="col">Tâche</th>
          <th scope="col">Catégorie</th>
          {colonnes.map((c) => <th key={c.cle} scope="col" className="v2-croise-num">{c.libelle}</th>)}
          <th scope="col" className="v2-croise-num">Total</th>
        </tr>
      </thead>
      <tbody>
        {lignes.map((l) => (
          <tr key={l.cle}>
            <td className="v2-croise-tache">{l.nom}</td>
            <td className="v2-cellule-discrete">{l.categorie}</td>
            {l.vals.map((h, i) => (
              <td key={colonnes[i].cle} className={`v2-croise-num${h > 0 ? " rempli" : " vide"}`}>{h > 0 ? `${h}h` : "—"}</td>
            ))}
            <td className="v2-croise-num v2-croise-total">{l.total}h</td>
          </tr>
        ))}
      </tbody>
      <tfoot>
        <tr>
          <th scope="row" colSpan={2}>Total</th>
          {totaux.map((t, i) => <td key={colonnes[i].cle} className="v2-croise-num">{t > 0 ? `${t}h` : "—"}</td>)}
          <td className="v2-croise-num">{total}h</td>
        </tr>
      </tfoot>
    </table>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

function FicheCollaborateurV2() {
  const { id } = useParams();
  const [collab, setCollab] = useState(null);
  const [timeData, setTimeData] = useState([]);
  const [projets, setProjets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [erreur, setErreur] = useState(null);
  const [exportEnCours, setExportEnCours] = useState(false);
  const pieRef = useRef(null);

  const initiale = semaineIso(new Date());
  const [periodType, setPeriodType] = useState("mois");
  const [semaineSel, setSemaineSel] = useState({ annee: initiale.annee, semaine: initiale.semaine });
  const [moisSel, setMoisSel] = useState({ annee: new Date().getFullYear(), mois: new Date().getMonth() + 1 });
  const [dateDebut, setDateDebut] = useState("");
  const [dateFin, setDateFin] = useState("");

  // L'annee de reference suit le mode : annee ISO en semaine, civile sinon
  const annee = periodType === "semaine" ? semaineSel.annee : moisSel.annee;
  const semaine = semaineSel.semaine;
  const mois = moisSel.mois;

  useEffect(() => {
    setLoading(true);
    getCollaborateur(parseInt(id))
      .then(setCollab)
      .catch((err) => setErreur(`Collaborateur non chargé : ${err.message}`))
      .finally(() => setLoading(false));
  }, [id]);

  useEffect(() => {
    if (!collab) return;
    const echec = (err) => setErreur(`Temps non chargés : ${err.message}`);
    setErreur(null);
    if (periodType === "semaine") {
      getFeuilleTemps(collab.id, annee, semaine).then(setTimeData).catch(echec);
    } else if (periodType === "mois") {
      getFeuilleTempsMois(collab.id, annee, mois).then(setTimeData).catch(echec);
    } else if (periodType === "personnalise") {
      // Rien a afficher tant que la plage n'est pas complete et valide
      if (!dateDebut || !dateFin || dateDebut > dateFin) {
        setTimeData([]);
        return;
      }
      getFeuilleTempsPeriode(collab.id, dateDebut, dateFin).then(setTimeData).catch(echec);
    }
  }, [collab, periodType, semaine, mois, annee, dateDebut, dateFin]);

  // Projets du collaborateur (acteur principal ou secondaire), en cours et en attente
  useEffect(() => {
    if (!collab) return;
    getMesProjets(collab.id).then(setProjets).catch(() => setProjets([]));
  }, [collab]);

  const semainePrecedente = () =>
    setSemaineSel(({ annee: a, semaine: s }) => (s > 1 ? { annee: a, semaine: s - 1 } : { annee: a - 1, semaine: nbSemainesIso(a - 1) }));
  const semaineSuivante = () =>
    setSemaineSel(({ annee: a, semaine: s }) => (s < nbSemainesIso(a) ? { annee: a, semaine: s + 1 } : { annee: a + 1, semaine: 1 }));
  const moisPrecedent = () =>
    setMoisSel(({ annee: a, mois: m }) => (m > 1 ? { annee: a, mois: m - 1 } : { annee: a - 1, mois: 12 }));
  const moisSuivant = () =>
    setMoisSel(({ annee: a, mois: m }) => (m < 12 ? { annee: a, mois: m + 1 } : { annee: a + 1, mois: 1 }));

  const getPeriodeLabel = () => {
    if (periodType === "semaine") return `Semaine ${semaine} — ${annee}`;
    if (periodType === "mois") return `${MOIS_NOMS[mois - 1]} ${annee}`;
    return `${dateDebut} → ${dateFin}`;
  };

  const exportPDF = async () => {
    setExportEnCours(true);
    try {
      const doc = new jsPDF({ orientation: "landscape" });

      // En-tete
      doc.setFontSize(16);
      doc.text(`Suivi des temps - ${collab.prenom} ${collab.nom}`, 14, 18);
      doc.setFontSize(10);
      doc.text(`Profil : ${collab.profil_nom}  |  Rôle : ${collab.role}  |  Email : ${collab.email}`, 14, 25);
      doc.text(`Période : ${getPeriodeLabel()}  |  Date d'export : ${new Date().toLocaleDateString("fr-FR")}`, 14, 31);

      // Indicateurs
      const totalHeures = timeData.reduce((sum, d) => sum + d.heures, 0);
      doc.setFontSize(11);
      doc.setFont(undefined, "bold");
      doc.text(`Total heures : ${totalHeures.toFixed(1)}h`, 14, 40);
      doc.text(`Lignes saisies : ${timeData.length}`, 80, 40);
      doc.setFont(undefined, "normal");

      // Repartition par tache
      const repartitionRows = repartitionParTache(timeData)
        .sort((a, b) => b.value - a.value)
        .map(({ label, value: h }) => [label, `${h}h`, `${((h / totalHeures) * 100).toFixed(1)}%`]);

      doc.setFontSize(10);
      doc.setFont(undefined, "bold");
      doc.text("Répartition par tâche", 14, 48);
      doc.setFont(undefined, "normal");
      autoTable(doc, {
        startY: 51,
        head: [["Tâche", "Heures", "% du total"]],
        body: repartitionRows,
        styles: { fontSize: 8 },
        headStyles: { fillColor: [40, 40, 40], textColor: [240, 240, 240] },
        tableWidth: 120,
        margin: { left: 14 },
      });

      const afterRepartition = doc.lastAutoTable.finalY + 8;

      // Camembert (capture) : fond lu sur le theme courant
      let afterGraph = afterRepartition;
      if (pieRef.current) {
        try {
          const fond = getComputedStyle(pieRef.current).getPropertyValue("--v2-card").trim() || "#2c2a27";
          const canvas = await html2canvas(pieRef.current, { backgroundColor: fond, scale: 1.5 });
          const imgData = canvas.toDataURL("image/png");
          const imgW = 120;
          const imgH = (canvas.height / canvas.width) * imgW * 1.15;
          doc.addImage(imgData, "PNG", 140, 51, imgW, imgH);
          afterGraph = Math.max(afterRepartition, 51 + imgH + 4);
        } catch (e) { console.warn("html2canvas camembert:", e); }
      }

      // Projets du collaborateur
      let afterProjets = afterGraph;
      if (projets.length > 0) {
        doc.setFontSize(10);
        doc.setFont(undefined, "bold");
        doc.text("Projets", 14, afterGraph + 6);
        doc.setFont(undefined, "normal");
        autoTable(doc, {
          startY: afterGraph + 9,
          head: [["Projet", "Rôle", "Avancement", "Étapes", "Date fin prévue"]],
          body: projets.map((p) => [
            p.nom,
            LIBELLES_ROLE_PROJET[p.mon_role] || p.mon_role,
            `${p.avancement || 0}%`,
            p.etapes_total ? `${p.etapes_terminees} / ${p.etapes_total}${p.etapes_en_retard ? ` (${p.etapes_en_retard} en retard)` : ""}` : "—",
            formatDate(p.date_fin_prevue),
          ]),
          styles: { fontSize: 8 },
          headStyles: { fillColor: [40, 40, 40], textColor: [240, 240, 240] },
          tableWidth: 160,
          margin: { left: 14 },
        });
        afterProjets = doc.lastAutoTable.finalY + 8;
      }

      // Detail : meme calcul que le tableau a l'ecran
      doc.setFontSize(10);
      doc.setFont(undefined, "bold");
      doc.text("Détail — " + getPeriodeLabel(), 14, afterProjets);
      doc.setFont(undefined, "normal");

      const croise = construireCroise(timeData, periodType, semaine, annee);
      const head = [["Tâche", "Cat.", ...croise.colonnes.map((c) => c.libelle), "Total"]];
      const body = croise.lignes.map((l) => [l.nom, l.categorie, ...l.vals.map((v) => (v > 0 ? `${v}h` : "—")), `${l.total}h`]);
      body.push(["Total", "", ...croise.totaux.map((t) => (t > 0 ? `${t}h` : "—")), `${totalHeures}h`]);

      autoTable(doc, {
        startY: afterProjets + 4,
        head,
        body,
        styles: { fontSize: 8 },
        headStyles: { fillColor: [40, 40, 40], textColor: [240, 240, 240] },
        bodyStyles: { textColor: [30, 30, 30] },
        didParseCell: (data) => {
          if (data.row.index === body.length - 1) data.cell.styles.fontStyle = "bold";
        },
      });

      doc.save(`fiche_${collab.nom}_${collab.prenom}_${getPeriodeLabel().replace(/ /g, "_")}.pdf`);
    } finally {
      setExportEnCours(false);
    }
  };

  if (loading) {
    return <div className="v2-page v2-page-module"><div className="v2-chargement">Chargement…</div></div>;
  }

  if (!collab) {
    return (
      <div className="v2-page v2-page-module">
        <div className="v2-msg v2-msg-erreur">{erreur || "Collaborateur introuvable."}</div>
      </div>
    );
  }

  const totalHeures = timeData.reduce((sum, d) => sum + d.heures, 0);
  const pieData = repartitionParTache(timeData);
  const croise = construireCroise(timeData, periodType, semaine, annee);

  return (
    <div className="v2-page v2-page-module">
      {/* ── En-tete : identite ── */}
      <div className="v2-fiche-entete">
        <Link to="/v2/pilotage/equipe" className="v2-bib-retour">
          <IconeV2 nom="retour" taille={13} /> Équipes
        </Link>
        <div className="v2-fiche-identite">
          <span className="v2-collab-avatar v2-fiche-avatar" aria-hidden="true">
            {`${(collab.prenom || "")[0] || ""}${(collab.nom || "")[0] || ""}`.toUpperCase()}
          </span>
          <div>
            <h1 className="v2-module-titre">{collab.prenom} <span className="v2-collab-nom-famille">{collab.nom}</span></h1>
            <p className="v2-fiche-meta">
              <span className={`v2-statut ${collab.role === "directeur" ? "v2-statut-fort" : "v2-statut-neutre"}`}>
                {LIBELLES_ROLES[collab.role] || collab.role}
              </span>
              <span>{collab.profil_nom}</span>
              <span className="v2-fiche-sep" aria-hidden="true">·</span>
              <span>{collab.email}</span>
            </p>
          </div>
        </div>
      </div>

      {/* ── Periode ── */}
      <div className="v2-barre-outils">
        <div className="v2-fiche-periode">
          <div className="v2-bascule" role="group" aria-label="Type de période">
            {[["semaine", "Semaine"], ["mois", "Mois"], ["personnalise", "Personnalisé"]].map(([p, lib]) => (
              <button key={p} type="button" className={`v2-bascule-choix${periodType === p ? " actif" : ""}`} aria-pressed={periodType === p} onClick={() => setPeriodType(p)}>
                {lib}
              </button>
            ))}
          </div>

          {periodType === "semaine" && (
            <div className="v2-semaine-nav">
              <button type="button" className="v2-btn v2-btn-secondaire v2-btn-icone" onClick={semainePrecedente} aria-label="Semaine précédente"><IconeV2 nom="retour" /></button>
              <span className="v2-semaine-titre">Semaine {semaine} — {annee}</span>
              <button type="button" className="v2-btn v2-btn-secondaire v2-btn-icone" onClick={semaineSuivante} aria-label="Semaine suivante"><IconeV2 nom="chevronDroite" /></button>
            </div>
          )}

          {periodType === "mois" && (
            <div className="v2-semaine-nav">
              <button type="button" className="v2-btn v2-btn-secondaire v2-btn-icone" onClick={moisPrecedent} aria-label="Mois précédent"><IconeV2 nom="retour" /></button>
              <span className="v2-semaine-titre">{MOIS_NOMS[mois - 1]} {annee}</span>
              <button type="button" className="v2-btn v2-btn-secondaire v2-btn-icone" onClick={moisSuivant} aria-label="Mois suivant"><IconeV2 nom="chevronDroite" /></button>
            </div>
          )}

          {periodType === "personnalise" && (
            <div className="v2-fiche-dates">
              <div className="v2-champ">
                <label className="v2-invisible" htmlFor="v2-fiche-debut">Date de début</label>
                <input id="v2-fiche-debut" type="date" value={dateDebut} onChange={(e) => setDateDebut(e.target.value)} />
              </div>
              <span aria-hidden="true">→</span>
              <div className="v2-champ">
                <label className="v2-invisible" htmlFor="v2-fiche-fin">Date de fin</label>
                <input id="v2-fiche-fin" type="date" value={dateFin} onChange={(e) => setDateFin(e.target.value)} />
              </div>
              {(!dateDebut || !dateFin) && <span className="v2-champ-aide">Choisissez une date de début et une date de fin.</span>}
              {dateDebut && dateFin && dateDebut > dateFin && (
                <span className="v2-champ-aide v2-texte-danger">La date de début suit la date de fin.</span>
              )}
            </div>
          )}
        </div>

        <button type="button" className="v2-btn v2-btn-secondaire" onClick={exportPDF} disabled={timeData.length === 0 || exportEnCours}>
          {exportEnCours ? "Export…" : "Export PDF"}
        </button>
      </div>

      {erreur && <div className="v2-msg v2-msg-erreur v2-marge-bas">{erreur}</div>}

      {/* ── Indicateurs ── */}
      <div className="v2-fiche-stats">
        <div className="v2-stat">
          <span className="v2-stat-libelle">Total heures</span>
          <span className="v2-stat-valeur">{totalHeures.toFixed(1)} h</span>
        </div>
        <div className="v2-stat">
          <span className="v2-stat-libelle">Lignes saisies</span>
          <span className="v2-stat-valeur">{timeData.length}</span>
        </div>
      </div>

      {/* ── Repartition + projets ── */}
      {(pieData.length > 0 || projets.length > 0) && (
        <div className="v2-fiche-grille">
          {pieData.length > 0 && (
            <section className="v2-panneau" ref={pieRef} aria-label="Répartition par tâche">
              <h2 className="v2-panneau-titre">Répartition par tâche</h2>
              <GraphiqueSecteurs data={pieData} unite="h" />
            </section>
          )}

          {projets.length > 0 && (
            <section className="v2-panneau v2-fiche-projets" aria-label="Projets">
              <h2 className="v2-panneau-titre">Projets</h2>
              <div className="v2-tableau-conteneur">
                <table className="v2-tableau">
                  <thead>
                    <tr>
                      <th scope="col">Projet</th>
                      <th scope="col">Rôle</th>
                      <th scope="col">Avancement</th>
                      <th scope="col">Étapes</th>
                      <th scope="col">Date fin prévue</th>
                    </tr>
                  </thead>
                  <tbody>
                    {projets.map((p) => {
                      const av = p.avancement || 0;
                      return (
                        <tr key={p.id}>
                          <td>
                            <Link to={`/v2/projets/${p.id}`}>{p.nom}</Link>
                            {p.en_retard && <> <span className="v2-statut v2-statut-danger">En retard</span></>}
                          </td>
                          <td>
                            <span className={`v2-statut${p.mon_role === "principal" ? " v2-statut-fort" : ""}`}>
                              {LIBELLES_ROLE_PROJET[p.mon_role] || p.mon_role}
                            </span>
                          </td>
                          <td>
                            <span className="v2-progression v2-progression-compacte">
                              <span className="v2-progression-piste">
                                <span className={`v2-progression-valeur v2-ton-${av >= 100 ? "success" : "fort"}`} style={{ width: `${Math.min(av, 100)}%` }} />
                              </span>
                              <span className="v2-progression-texte">{av}%</span>
                            </span>
                          </td>
                          <td className="v2-cellule-discrete">
                            {p.etapes_total ? `${p.etapes_terminees} / ${p.etapes_total}` : "—"}
                            {p.etapes_en_retard > 0 && <span className="v2-texte-danger"> · {p.etapes_en_retard} en retard</span>}
                          </td>
                          <td className="v2-cellule-discrete">{formatDate(p.date_fin_prevue)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </section>
          )}
        </div>
      )}

      {/* ── Detail ── */}
      <section aria-labelledby="v2-fiche-detail-titre">
        <div className="v2-section-titre">
          <h2 id="v2-fiche-detail-titre">Détail — {getPeriodeLabel()}</h2>
          <span className="v2-section-filet" />
        </div>
        {timeData.length === 0 ? (
          <div className="v2-panneau"><p className="v2-vide">Aucune saisie sur la période.</p></div>
        ) : (
          <div className="v2-panneau v2-tableau-conteneur">
            <TableauCroise croise={croise} />
          </div>
        )}
      </section>
    </div>
  );
}

export default FicheCollaborateurV2;
